// Marketing tab — ad spend manual entry + ROAS/CPA/MER.
import { useEffect, useState } from "react";
import { AreaChart, Area, Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Megaphone, Plus, Activity, Target, TrendingUp, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getMarketing } from "@/lib/analytics";
import { KpiCard, fmtMoney, fmtInt } from "../components/KpiCard";
import { Card } from "./OverviewTab";
import AdSpendDialog from "../components/AdSpendDialog";

const COLOR = { primary: "#E91E63", emerald: "#22C55E", amber: "#F59E0B", rose: "#F43F5E", cyan: "#06B6D4" };
const CHANNEL_COLOR = { meta: "#1877F2", tiktok: "#FF0050", google: "#34A853", other: "#71717a" };
const tip = { contentStyle: { background: "rgba(15, 15, 23, 0.95)", border: "1px solid rgba(91,91,214,0.4)", borderRadius: 8, fontSize: 12 } };

export default function MarketingTab({ range }) {
  const [data, setData] = useState(null);
  const [version, setVersion] = useState(0);
  useEffect(() => { getMarketing(range).then(setData); }, [JSON.stringify(range), version]); // eslint-disable-line
  if (!data) return <div className="h-96 rounded-xl bg-card/40 border border-border animate-pulse" />;
  const t = data.totals;
  const noSpend = !data.totals.spend;

  return (
    <div className="space-y-6" data-testid="analytics-marketing">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-muted-foreground">
          Introduceți cheltuielile zilnice manual. Integrarea Meta/TikTok/Google va veni ulterior.
        </div>
        <AdSpendDialog
          range={range}
          onChanged={() => setVersion((v) => v + 1)}
          trigger={<Button size="sm" data-testid="open-adspend"><Plus className="h-3.5 w-3.5 mr-1.5" /> Gestionează spend</Button>}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Ad Spend total" value={t.spend} format="money" icon={Megaphone} color={COLOR.rose} />
        <KpiCard label="ROAS" value={t.roas} format="ratio" icon={Activity} color={COLOR.emerald} />
        <KpiCard label="MER" value={t.mer} format="ratio" icon={TrendingUp} color={COLOR.amber} />
        <KpiCard label="CPA" value={t.cpa} format="money" icon={Target} color={COLOR.primary} />
        <KpiCard label="Profit după ads (brut)" value={t.profit_after_ads} format="money" icon={DollarSign} color={COLOR.emerald} />
        <KpiCard label="Vânzări atribuite" value={t.revenue} format="money" icon={DollarSign} color={COLOR.primary} />
        <KpiCard label="Comenzi" value={t.orders} icon={Megaphone} color={COLOR.primary} />
      </div>

      {noSpend && (
        <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-4 text-sm text-amber-200 flex items-start gap-3">
          <Megaphone className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold mb-1">Nicio cheltuială Ads înregistrată în interval.</div>
            <div className="text-xs">Adaugă manual spend-ul zilnic folosind butonul de mai sus pentru a calcula ROAS / MER / CPA real.</div>
          </div>
        </div>
      )}

      {/* Spend vs Revenue */}
      <Card title="Spend vs Vânzări (zilnic)">
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={data.series}>
            <defs>
              <linearGradient id="m-rev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={COLOR.emerald} stopOpacity={0.4} />
                <stop offset="100%" stopColor={COLOR.emerald} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="m-sp" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={COLOR.rose} stopOpacity={0.4} />
                <stop offset="100%" stopColor={COLOR.rose} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.04)" />
            <XAxis dataKey="date" stroke="#71717a" fontSize={10} tickFormatter={(d) => d.slice(5)} />
            <YAxis stroke="#71717a" fontSize={10} />
            <Tooltip {...tip} formatter={(v) => fmtMoney(v) + " RON"} />
            <Area type="monotone" dataKey="revenue" stroke={COLOR.emerald} strokeWidth={2} fill="url(#m-rev)" />
            <Area type="monotone" dataKey="spend" stroke={COLOR.rose} strokeWidth={2} fill="url(#m-sp)" />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="ROAS zilnic">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.series}>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="date" stroke="#71717a" fontSize={10} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#71717a" fontSize={10} />
              <Tooltip {...tip} formatter={(v) => Number(v).toFixed(2) + "×"} />
              <Bar dataKey="roas" fill={COLOR.amber} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="CPA zilnic">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.series}>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="date" stroke="#71717a" fontSize={10} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#71717a" fontSize={10} />
              <Tooltip {...tip} formatter={(v) => fmtMoney(v) + " RON"} />
              <Line type="monotone" dataKey="cpa" stroke={COLOR.primary} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Pe canal">
          {data.by_channel.length === 0 ? (
            <div className="text-center text-xs text-muted-foreground py-8">Adaugă cheltuieli pentru a vedea breakdown</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={150}>
                <PieChart>
                  <Pie data={data.by_channel} dataKey="spend" innerRadius={40} outerRadius={65}>
                    {data.by_channel.map((c) => <Cell key={c.channel} fill={CHANNEL_COLOR[c.channel] || COLOR.primary} />)}
                  </Pie>
                  <Tooltip {...tip} formatter={(v) => fmtMoney(v) + " RON"} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1 mt-2">
                {data.by_channel.map((c) => (
                  <div key={c.channel} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2 capitalize"><i className="h-2.5 w-2.5 rounded-full" style={{ background: CHANNEL_COLOR[c.channel] || COLOR.primary }} /> {c.channel}</span>
                    <span className="tabular-nums">{fmtMoney(c.spend)} · ROAS {c.roas.toFixed(2)}×</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
